import React from 'react';
import { motion } from 'framer-motion';
import { 
  DollarSign, 
  BarChart3, 
  LineChart, 
  AlertCircle, 
  CheckCircle2 
} from 'lucide-react';

const FinancialsSection = ({ company }) => {
  if (!company.financials || Object.keys(company.financials).length === 0) {
    return null;
  }

  // Check if there are any non-empty financial values
  const hasFinancialData = Object.entries(company.financials).some(([key, value]) => 
    key.startsWith('year') ? value && value.trim() !== '' : value && value.trim() !== ''
  );

  if (!hasFinancialData) {
    return null;
  }

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white/95 backdrop-blur-lg rounded-3xl p-6 sm:p-10 shadow-2xl mb-8">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 flex items-center gap-2">
        <DollarSign className="w-6 h-6 text-green-500" /> Financials
      </h2>
      {Object.entries(company.financials)
        .filter(([key, value]) => key.startsWith('year') && value && value.trim() !== '')
        .map(([key, value]) => (
          <div key={key} className="bg-green-50 rounded-xl p-4 text-center">
            <div className="text-lg font-bold text-green-900">{value}</div>
            <div className="text-xs text-green-700">{key.replace('year', 'Year ')}</div>
          </div>
        ))}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        {company.financials.burnRate && company.financials.burnRate.trim() !== '' && (
          <div className="bg-white rounded-xl p-4 flex items-center gap-3">
            <BarChart3 className="w-5 h-5 text-blue-500" /> 
            <span className="font-medium">Burn Rate:</span> {company.financials.burnRate}
          </div>
        )}
        {company.financials.cac && company.financials.cac.trim() !== '' && (
          <div className="bg-white rounded-xl p-4 flex items-center gap-3">
            <LineChart className="w-5 h-5 text-purple-500" /> 
            <span className="font-medium">CAC:</span> {company.financials.cac}
          </div>
        )}
        {company.financials.ltv && company.financials.ltv.trim() !== '' && (
          <div className="bg-white rounded-xl p-4 flex items-center gap-3">
            <DollarSign className="w-5 h-5 text-green-500" /> 
            <span className="font-medium">LTV:</span> {company.financials.ltv}
          </div>
        )}
        {company.financials.churn && company.financials.churn.trim() !== '' && (
          <div className="bg-white rounded-xl p-4 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500" /> 
            <span className="font-medium">Churn:</span> {company.financials.churn}
          </div>
        )}
        {company.financials.breakeven && company.financials.breakeven.trim() !== '' && (
          <div className="bg-white rounded-xl p-4 flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-600" /> 
            <span className="font-medium">Breakeven:</span> {company.financials.breakeven}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default FinancialsSection; 